import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import colors from '../../../styles/colors';
import SideNavHeader, {
    ISideNavHeaderProps
} from '../SideNavHeader/SideNavHeader';
import SideNavFooter from '../SideNavFooter/SideNavFooter';
import SideNavMenuContainer from '../SideNavMenu/SideNavMenuContainer';
import { IMenuItemContainer } from '../SideNavMenuItem/SideNavMenuItemContainer';
import { ISideNavItem } from '../HBSideNav';
import { useSideDrawer } from '../../HBSideDrawer/HBSideDrawer';
import { customScrollBar } from '../../../styles/scrollbar';
import { useSideNavContext } from '../context/SideNavProvider';
import { ISideNavTheme } from '../context/useCreateTheme';
import { transparentize } from 'polished';

export type PreContentRendererProps = {
    theme: ISideNavTheme;
};

export type PostContentRendererProps = {
    theme: ISideNavTheme;
};

export type ItemOpenChangeDetails = {
    label: string;
    isOpen: boolean;
    trigger: 'click' | 'route';
};

const SideNavContent = ({
    menuItems,
    footerMenuItems,
    headerProps,
    activeItem,
    footerTitle,
    customHeaderRenderer,
    preContentRenderer,
    postContentRenderer,
    preContentRendererMenuItems,
    onItemOpenChange
}: {
    menuItems: ISideNavItem[];
    footerMenuItems: ISideNavItem[];
    headerProps?: ISideNavHeaderProps;
    activeItem?: string;
    customHeaderRenderer?: (headerProps: ISideNavHeaderProps) => JSX.Element;
    preContentRenderer?: ({ theme }: PreContentRendererProps) => JSX.Element;
    postContentRenderer?: ({
        theme
    }: PostContentRendererProps) => React.ReactNode;
    footerTitle?: () => JSX.Element;
    preContentRendererMenuItems?: ISideNavItem[];
    onItemOpenChange?: (details: ItemOpenChangeDetails) => void;
}) => {
    const { isPinned, isMinimized } = useSideDrawer();
    const [selectedItem, setSelectedItem] = useState<string | null>(
        activeItem || null
    );
    const [selectedSubItem, setSelectedSubItem] = useState<string | null>(null);
    const [openItem, setOpenItem] = useState<string | null>(null);
    const [persistentItemOpen, setPersistentItemOpen] =
        useState<ISideNavItem>();

    const { theme } = useSideNavContext();

    const changeOpenItem = useCallback(
        (next: string | null, trigger: ItemOpenChangeDetails['trigger']) => {
            if (next === openItem) {
                return;
            }
            if (openItem) {
                onItemOpenChange?.({
                    label: openItem,
                    isOpen: false,
                    trigger
                });
            }
            if (next) {
                onItemOpenChange?.({ label: next, isOpen: true, trigger });
            }
            setOpenItem(next);
        },
        [openItem, onItemOpenChange]
    );

    const setSelectedItems = useCallback(
        (
            details: {
                parentLabel?: string;
                label: string;
                subItems?: ISideNavItem[];
                preventCloseOnClick?: boolean;
            },
            trigger: ItemOpenChangeDetails['trigger']
        ) => {
            if (!details) {
                setSelectedItem(null);
                setSelectedSubItem(null);
                changeOpenItem(null, trigger);
                return;
            }

            const { parentLabel, label, subItems, preventCloseOnClick } =
                details;

            if (preventCloseOnClick) {
                return;
            }

            if (parentLabel) {
                setSelectedItem?.(parentLabel);
                setSelectedSubItem?.(label);

                changeOpenItem(parentLabel, trigger);
                return;
            }

            setSelectedItem?.(label);
            setSelectedSubItem?.(subItems?.[0]?.label);

            if (subItems && subItems.length > 0) {
                if (openItem !== label) {
                    const ev = new MouseEvent(
                        'click'
                    ) as unknown as React.MouseEvent<HTMLElement, MouseEvent>;
                    subItems[0]?.onClick?.(ev, {
                        item: {
                            label: subItems[0].label
                        },
                        parentLabel: label,
                        source: 'sidenav',
                        trigger: 'auto'
                    });
                }

                changeOpenItem(openItem === label ? null : label, trigger);
            } else {
                changeOpenItem(null, trigger);
            }
        },
        [setSelectedItem, setSelectedSubItem, changeOpenItem, openItem]
    );

    useEffect(
        () => {
            if (!activeItem) {
                setSelectedSubItem(null);
                return;
            }
            [
                ...menuItems,
                ...footerMenuItems,
                ...(preContentRendererMenuItems || [])
            ].forEach(item => {
                if (item.subItems) {
                    item.subItems.forEach(subItem => {
                        if (subItem.label === activeItem) {
                            setSelectedItems(
                                {
                                    parentLabel: item.label,
                                    label: subItem.label,
                                    subItems: item.subItems
                                },
                                'route'
                            );
                        }
                    });
                } else {
                    if (item.label === activeItem) {
                        setSelectedItems({ label: item.label }, 'route');
                        return;
                    }
                }
            });
        },
        [activeItem] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleItemClick: IMenuItemContainer['onMenuItemClick'] = useCallback(
        (_e, item) => {
            if (!!item) {
                const {
                    item: { label, subItems },
                    parentLabel
                } = item;
                setSelectedItems({ parentLabel, label, subItems }, 'click');
            } else {
                setSelectedItems(null, 'click');
            }
        },
        [setSelectedItems]
    );

    return (
        <SideNavWrapper
            $theme={theme}
            isPinned={isPinned}
            isMinimized={isMinimized}
        >
            {customHeaderRenderer?.(headerProps) ?? (
                <SideNavHeader {...headerProps} />
            )}
            {preContentRenderer?.({ theme })}
            <ScrollableContainer>
                <SideNavMenuContainer
                    data-testid="menu"
                    menuItems={menuItems}
                    selectedItem={selectedItem}
                    setSelectedItem={setSelectedItem}
                    selectedSubItem={selectedSubItem}
                    openItem={openItem}
                    handleItemClick={handleItemClick}
                    persistentItemOpen={persistentItemOpen}
                    setPersistentItemOpen={setPersistentItemOpen}
                />
            </ScrollableContainer>
            <FooterContainer $theme={theme}>
                {postContentRenderer?.({ theme })}
                <SideNavFooter
                    footerMenuItems={footerMenuItems}
                    selectedItem={selectedItem}
                    selectedSubItem={selectedSubItem}
                    handleItemClick={handleItemClick}
                    persistentItemOpen={persistentItemOpen}
                    setPersistentItemOpen={setPersistentItemOpen}
                    footerTitle={footerTitle}
                />
            </FooterContainer>
        </SideNavWrapper>
    );
};

export default SideNavContent;

// -------------------------------- Styles -------------------------------- //
const SideNavWrapper = styled.nav<{
    $theme: ISideNavTheme;
    isPinned: boolean;
    isMinimized: boolean;
}>`
    display: flex;
    overflow: hidden;
    flex-direction: column;
    height: 100%;
    width: 100%;
    background-color: ${({ $theme }) => $theme.backgroundColor};

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
        box-shadow: ${({ isPinned, isMinimized }) =>
            !isPinned && !isMinimized ? `0 0 40px ${colors.black_04}` : 'none'};
        transition: box-shadow 50ms cubic-bezier(0.77, 0, 0.175, 1);
    }
`;

const ScrollableContainer = styled.div`
    ${customScrollBar};
    &::-webkit-scrollbar {
        background-color: transparent;
        &-thumb {
            background-color: ${colors.neutral800};
            border: 1px solid transparent;
            background-clip: padding-box;
            cursor: pointer;
            &:hover {
                background-color: ${colors.neutral700};
            }
        }
    }
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    flex: 1;
    padding: 8px 2px 15px 8px;
`;

const FooterContainer = styled.div<{ $theme: ISideNavTheme }>`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 8px 12px 8px;

    &::before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 6px;
        height: 30px;
        pointer-events: none;
        ${({ $theme }) => `
            background: linear-gradient(
                180deg,
                ${transparentize(1, $theme.backgroundColor)} 0%,
                ${transparentize(0, $theme.backgroundColor)} 100%
                );
            `};
    }
`;
